import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, HeartPulse, UserRound } from 'lucide-react';
import api from '@/lib/api';
import MeasurementSummaryCards from '@/components/measurements/MeasurementSummaryCards';
import WeightChart from '@/components/measurements/WeightChart';
import MeasurementList from '@/components/measurements/MeasurementList';
import MeasurementForm from '@/components/measurements/MeasurementForm';

export default function AdminMeasurements() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [measurements, setMeasurements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingMeasurement, setEditingMeasurement] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [customerPage, setCustomerPage] = useState(1);
    const [customerMeta, setCustomerMeta] = useState({});

    // Pre-select customer from URL param
    useEffect(() => {
        const cid = searchParams.get('customer');
        if (cid && customers.length > 0) {
            const c = customers.find(x => String(x.id) === cid);
            if (c) setSelectedCustomer(c);
        }
    }, [searchParams, customers]);

    const fetchCustomers = useCallback(async () => {
        try {
            const res = await api.get('/admin/customers', {
                params: { search: customerSearch, page: customerPage, per_page: 10 },
            });
            setCustomers(res.data.data);
            setCustomerMeta({ last_page: res.data.last_page, current_page: res.data.current_page });
        } catch {}
    }, [customerSearch, customerPage]);

    useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

    const handleSelectCustomer = (customer) => {
        setSelectedCustomer(customer);
        setSearchParams({ customer: customer.id });
    };

    const fetchMeasurements = useCallback(async () => {
        if (!selectedCustomer) return;
        setLoading(true);
        setError('');
        try {
            const res = await api.get(`/admin/customers/${selectedCustomer.id}/measurements`);
            setMeasurements(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al cargar mediciones.');
        } finally { setLoading(false); }
    }, [selectedCustomer]);

    useEffect(() => { fetchMeasurements(); }, [fetchMeasurements]);

    const handleSave = async (payload) => {
        setSaving(true);
        setFormError('');
        try {
            if (editingMeasurement) {
                await api.put(`/admin/customers/${selectedCustomer.id}/measurements/${editingMeasurement.id}`, payload);
            } else {
                await api.post(`/admin/customers/${selectedCustomer.id}/measurements`, payload);
            }
            setDialogOpen(false);
            setEditingMeasurement(null);
            fetchMeasurements();
        } catch (err) {
            setFormError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleEdit = (m) => {
        setEditingMeasurement(m);
        setFormError('');
        setDialogOpen(true);
    };

    const handleDelete = async (m) => {
        if (!confirm('¿Eliminar esta medición?')) return;
        try {
            await api.delete(`/admin/customers/${selectedCustomer.id}/measurements/${m.id}`);
            fetchMeasurements();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al eliminar.');
        }
    };

    const openCreate = () => {
        setEditingMeasurement(null);
        setFormError('');
        setDialogOpen(true);
    };

    const latest = measurements[0] || null;

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide">Mediciones</h1>
                {selectedCustomer && (
                    <Button onClick={openCreate} className="w-full sm:w-auto">
                        <Plus className="h-4 w-4 mr-2" />Nueva Medición
                    </Button>
                )}
            </div>

            {!selectedCustomer ? (
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <Search className="h-4 w-4 text-white/60" />
                            <Input
                                placeholder="Buscar alumno..."
                                value={customerSearch}
                                onChange={e => { setCustomerSearch(e.target.value); setCustomerPage(1); }}
                                className="max-w-sm"
                            />
                        </div>
                        {customers.length === 0 ? (
                            <p className="text-white/60 text-center py-8">No se encontraron alumnos.</p>
                        ) : (
                            <>
                                <div className="grid gap-2">
                                    {customers.map(c => (
                                        <button
                                            key={c.id}
                                            onClick={() => handleSelectCustomer(c)}
                                            className="flex items-center gap-3 p-3 rounded-md border border-hairline-dark bg-[#0a0a0a] text-left hover:bg-white/5 transition-colors"
                                        >
                                            <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center shrink-0">
                                                <UserRound className="h-5 w-5 text-white/60" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-white text-sm">{c.first_name} {c.last_name}</p>
                                                <p className="text-xs text-white/60">DNI: {c.dni} {c.gym?.name ? `· ${c.gym.name}` : ''}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                {customerMeta.last_page > 1 && (
                                    <div className="flex justify-center gap-2 mt-4">
                                        <Button variant="outline" size="sm" disabled={customerPage <= 1} onClick={() => setCustomerPage(p => p - 1)}>Anterior</Button>
                                        <span className="flex items-center text-sm text-white/60">Página {customerMeta.current_page} de {customerMeta.last_page}</span>
                                        <Button variant="outline" size="sm" disabled={customerPage >= customerMeta.last_page} onClick={() => setCustomerPage(p => p + 1)}>Siguiente</Button>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center">
                                <HeartPulse className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <p className="font-medium text-white">{selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                                <p className="text-xs text-white/60">DNI: {selectedCustomer.dni} {selectedCustomer.gym?.name ? `· ${selectedCustomer.gym.name}` : ''}</p>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => {
                            setSelectedCustomer(null);
                            setMeasurements([]);
                            setSearchParams({});
                        }}>
                            Cambiar alumno
                        </Button>
                    </div>

                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : error ? (
                        <div className="bg-white/5 text-white text-sm p-3 rounded-md">{error}</div>
                    ) : (
                        <>
                            <MeasurementSummaryCards latest={latest} />
                            <WeightChart measurements={measurements} />
                            <MeasurementList measurements={measurements} onEdit={handleEdit} onDelete={handleDelete} />
                        </>
                    )}
                </div>
            )}

            <MeasurementForm
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                measurement={editingMeasurement}
                onSave={handleSave}
                saving={saving}
                error={formError}
            />
        </div>
    );
}
